import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmountCentsBasedOnCurrency } from '@unified-checkout/kit-components';
import { Badge, ItemSelector } from '@unified-checkout/design-system';
import Arrow from '../../assets/icons/arrow.png';
import Close from '../../assets/icons/close.png';
import styles from '../card.module.scss';

export interface InstallmentPlan {
  id: string;
  amount: number;
  months: number;
  noFees?: boolean;
  fees?: number;
  zeroInterest?: boolean;
  bestChoice?: boolean;
  rate?: number;
  extra?: {
    downPayment?: number;
    adminFees?: number;
    purchaseFees?: number;
  };
}

export type InstallmentPlansCheckResult = {
  plans: InstallmentPlan[];
  minimumAmount?: number;
};

export type InstallmentContext = {
  hasSelectedPlan: boolean;
  selectedPlanData: InstallmentPlan | null;
  hasLoadedPlans: boolean;
};

export const INSTALLMENT_BIN_LENGTH = 8;

export type CardToInstallmentProps = {
  mode: 'bin' | 'token';
  cardBin?: string;
  cardToken?: string;
  hideUI?: boolean;
  onCheckInstallmentPlans?: (data: Record<string, unknown>) => Promise<InstallmentPlansCheckResult>;
  onSelectedInstallmentPlanChange?: (plan: InstallmentPlan | null) => void;
  onInstallmentContextChange?: (ctx: InstallmentContext) => void;
  displayCurrency: string;
  intentionCurrency: string;
  planAmountCurrency: string;
  tPaymentLayout: (key: string) => string;
};

export function CardToInstallment({
  mode,
  cardBin = '',
  cardToken,
  hideUI = false,
  onCheckInstallmentPlans,
  onSelectedInstallmentPlanChange,
  onInstallmentContextChange,
  displayCurrency,
  intentionCurrency,
  planAmountCurrency,
  tPaymentLayout,
}: CardToInstallmentProps) {
  const { t } = useTranslation('card');
  const [plans, setPlans] = useState<InstallmentPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [minimumPlanAmount, setMinimumPlanAmount] = useState(0);

  const plansRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPlansBinRef = useRef<string | null>(null);
  const plansRequestSeqRef = useRef(0);

  const contextCallbackRef = useRef(onInstallmentContextChange);
  contextCallbackRef.current = onInstallmentContextChange;

  const onSelectedChangeRef = useRef(onSelectedInstallmentPlanChange);
  onSelectedChangeRef.current = onSelectedInstallmentPlanChange;

  const visiblePlans = showAll ? plans : plans.slice(0, 2);
  const hasAnyZeroInterestPlan = useMemo(
    () => plans.some((p) => Boolean(p.zeroInterest)),
    [plans]
  );

  const selectedPlanData = useMemo(
    () => (selectedPlan ? plans.find((p) => p.id === selectedPlan) ?? null : null),
    [selectedPlan, plans]
  );
  const hasLoadedPlans = !plansLoading && plans.length > 0;
  const hasSelectedPlan = !!selectedPlan;

  useEffect(() => {
    contextCallbackRef.current?.({
      hasSelectedPlan,
      selectedPlanData,
      hasLoadedPlans,
    });
  }, [hasSelectedPlan, selectedPlanData, hasLoadedPlans]);

  const handleSelectPlan = (plan: InstallmentPlan) => {
    setSelectedPlan(plan.id);
    onSelectedChangeRef.current?.(plan);
  };

  const clearPlansState = () => {
    setPlans([]);
    setMinimumPlanAmount(0);
    setSelectedPlan(null);
    setShowAll(false);
    onSelectedChangeRef.current?.(null);
  };

  useEffect(() => {
    if (!onCheckInstallmentPlans) {
      if (plansRef.current) {
        clearTimeout(plansRef.current);
        plansRef.current = null;
      }
      lastPlansBinRef.current = null;
      setPlansLoading(false);
      clearPlansState();
      return;
    }

    if (mode === 'bin') {
      const bin = cardBin;

      if (bin.length < INSTALLMENT_BIN_LENGTH) {
        plansRequestSeqRef.current += 1;
        if (plansRef.current) {
          clearTimeout(plansRef.current);
          plansRef.current = null;
        }
        setPlans([]);
        setMinimumPlanAmount(0);
        setSelectedPlan(null);
        setShowAll(false);
        onSelectedChangeRef.current?.(null);
        lastPlansBinRef.current = null;
        return;
      }

      if (lastPlansBinRef.current === bin) return;
      lastPlansBinRef.current = bin;

      if (plansRef.current) clearTimeout(plansRef.current);
      plansRef.current = setTimeout(async () => {
        const requestSeq = (plansRequestSeqRef.current += 1);
        setPlansLoading(true);
        try {
          const result = await onCheckInstallmentPlans({ cardBin: bin });
          if (plansRequestSeqRef.current !== requestSeq) return;
          setPlans(Array.isArray(result.plans) ? result.plans : []);
          setMinimumPlanAmount(result.minimumAmount ?? 0);
          setSelectedPlan(null);
          setShowAll(false);
          onSelectedChangeRef.current?.(null);
        } catch {
          if (plansRequestSeqRef.current !== requestSeq) return;
          setPlans([]);
          setMinimumPlanAmount(0);
          setSelectedPlan(null);
          setShowAll(false);
          onSelectedChangeRef.current?.(null);
        } finally {
          if (plansRequestSeqRef.current === requestSeq) setPlansLoading(false);
        }
        plansRef.current = null;
      }, 400);

      return () => {
        if (plansRef.current) clearTimeout(plansRef.current);
      };
    }

    if (mode === 'token') {
      const token = cardToken;
      if (!token) {
        setPlans([]);
        setMinimumPlanAmount(0);
        setSelectedPlan(null);
        setShowAll(false);
        setPlansLoading(false);
        onSelectedChangeRef.current?.(null);
        return;
      }

      const controller = new AbortController();
      const loadPlans = async () => {
        setPlansLoading(true);
        try {
          const result = await onCheckInstallmentPlans({
            cardToken: token,
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          setPlans(Array.isArray(result.plans) ? result.plans : []);
          setMinimumPlanAmount(result.minimumAmount ?? 0);
          setSelectedPlan(null);
          setShowAll(false);
          onSelectedChangeRef.current?.(null);
        } catch (err) {
          const isAbortError =
            controller.signal.aborted ||
            (err instanceof DOMException && err.name === 'AbortError') ||
            (typeof err === 'object' &&
              err !== null &&
              'name' in err &&
              (err as { name?: string }).name === 'AbortError');

          if (isAbortError) return;

          setPlans([]);
          setSelectedPlan(null);
          setShowAll(false);
          onSelectedChangeRef.current?.(null);
        } finally {
          if (controller.signal.aborted) return;
          setPlansLoading(false);
        }
      };

      void loadPlans();
      return () => {
        controller.abort();
      };
    }
  }, [mode, cardBin, cardToken, onCheckInstallmentPlans]);

  if (!onCheckInstallmentPlans) {
    return null;
  }

  if (hideUI) {
    return null;
  }

  return (
    <>
      {plansLoading && (
        <div className="mt-4 text-sm text-gray-400">{t('INSTALLMENT_LOADING_PLANS')}</div>
      )}
      {!plansLoading && plans.length > 0 && (
        <div className="w-full border border-[#EAECF0] rounded-[12px] p-4 mt-4 bg-white">
          <div
            className={`flex items-center mb-[14px] ${
              hasAnyZeroInterestPlan ? 'justify-between' : 'justify-end'
            }`}
          >
            {hasAnyZeroInterestPlan && (
              <span className="text-[14px] font-semibold text-[#101828]">
                {t('INSTALLMENT_ZERO_PERCENT_PLANS_TITLE')}
              </span>
            )}
            <span className="text-[14px] leading-[24px] tracking-[0.16px] text-[#101828]">
              <strong className="font-semibold">
                {t('INSTALLMENT_STARTING')}{' '}
                {formatAmountCentsBasedOnCurrency(minimumPlanAmount, intentionCurrency)}{' '}
                {tPaymentLayout(displayCurrency)}
                {t('INSTALLMENT_PER_MONTH_SHORT')}
              </strong>
            </span>
          </div>

          <div className="flex flex-col gap-2">
            {visiblePlans.map((plan: InstallmentPlan) => {
              const isSelected = selectedPlan === plan.id;
              return (
                <div className="relative" key={plan.id}>
                  {plan.bestChoice && (
                    <span className="absolute top-[-0.7rem] left-[-0.5rem] bg-blue-500 text-white text-xs px-2 py-1 rounded">
                      {t('INSTALLMENT_BEST_CHOICE')}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <div className={`flex-1 ${styles.plan}`}>
                      <ItemSelector
                        id={plan.id}
                        type="radio"
                        size="sm"
                        selected={isSelected}
                        onClickHandler={() => handleSelectPlan(plan)}
                        text={
                          (
                            <div className="flex justify-between w-full">
                              <span>
                                {tPaymentLayout(displayCurrency)}{' '}
                                {formatAmountCentsBasedOnCurrency(plan.amount, planAmountCurrency)}
                                {t('PAY_PER_MONTH')}
                              </span>
                              <span>{t('INSTALLMENT_N_MONTHS', { months: plan.months })}</span>
                            </div>
                          ) as any
                        }
                        hintText={
                          (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {plan.noFees && (
                                <Badge text={t('INSTALLMENT_BADGE_NO_FEES')} color="green" size="sm" />
                              )}
                              {Number(plan.fees ?? plan.extra?.purchaseFees ?? 0) > 0 && (
                                <span className="text-[#475467] text-[14px] font-normal leading-[18px] tracking-[0.16px]">
                                  {t('INSTALLMENT_INCLUDING')} {tPaymentLayout(displayCurrency)}{' '}
                                  {formatAmountCentsBasedOnCurrency(
                                    Number(plan.fees ?? plan.extra?.purchaseFees ?? 0),
                                    intentionCurrency
                                  )}{' '}
                                  {t('INSTALLMENT_FEES_WORD')}
                                </span>
                              )}
                              {plan.zeroInterest && (
                                <Badge text={t('INSTALLMENT_BADGE_ZERO_INTEREST')} color="green" size="sm" />
                              )}
                            </div>
                          ) as any
                        }
                      />
                    </div>
                    {isSelected && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedPlan(null);
                          onSelectedChangeRef.current?.(null);
                        }}
                        className="w-8 h-8 flex-shrink-0 rounded-full border border-[#D0D5DD] bg-white flex items-center justify-center cursor-pointer p-0"
                      >
                        <img src={Close} alt={t('INSTALLMENT_CLOSE_PLAN_ALT')} className="w-[10px] h-[10px]" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {plans.length > 2 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAll((prev) => !prev);
              }}
              className="mt-3.5 bg-none border-none p-0 cursor-pointer text-[#144DFF] text-[15px] font-medium flex items-center gap-2"
            >
              {showAll ? t('INSTALLMENT_SHOW_LESS_PLANS') : t('INSTALLMENT_SHOW_MORE_PLANS')}
              <img
                src={Arrow}
                alt={t('INSTALLMENT_PLANS_LIST_ALT')}
                className={`w-[10px] h-[4px] transition-transform duration-200 ${showAll ? 'rotate-0' : 'rotate-180'}`}
              />
            </button>
          )}
        </div>
      )}
    </>
  );
}

export default CardToInstallment;
